// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { head, get, filter, flow, map, sortBy, uniqBy } from 'lodash/fp';
import moment from 'moment';
import Helmet from 'react-helmet';
import { compose, withProps } from 'recompose';
import download from 'downloadjs';
import t from 'app/utils/translate';
import Section from 'app/components/commons/Section';
import Icon from 'app/components/commons/Icon';
import Countdown from 'app/components/commons/Countdown';
import CSVUploadButton from 'app/components/commons/CSVUploadButton';
import Button from 'app/components/commons/Button';
import Spinner from 'app/components/commons/Spinner';
import Rows from 'app/components/layout/Rows';
import FlexiSelect from 'app/components/commons/FlexiSelect';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import withAPICollection from 'app/hoc/withAPICollection';
import { getSlates } from 'app/api/h2h';
import { decodeSlate } from 'app/scenes/h2h/LoadCreateChallenge';
import { fileToDataUrl } from 'app/utils/fileUtils';

type SelectOption = {
  id: number,
  name: string
};

type Props = {
  fileName?: string,
  siteName: string,
  slates: Array<Object>,
  competitionTypes: Array<SelectOption>,
  onFetch: () => void,
  onRedirect: () => void,
  downloadFunc: query => void,
  uploadFunc: data => void,
  downloadButtonLabel?: string,
  uploadLabel?: string
};

type State = {
  selectedChallengeRuleType?: Object,
  selectedCompetitionType?: Object,
  selectedSlate?: Array<Object>,
  selectedStartTime?: Object,
  isFileRejected: boolean,
  downloadError?: string,
  downloading?: boolean,
  uploading?: boolean,
  succeededLineups?: number,
  failedLineups?: Array<string>,
  totalLineups?: number
};

const Column = styled('div')({
  display: 'flex',
  flexDirection: 'column'
});

function filterChallengeRuleTypes(items, competitionType) {
  const slates = items.filter(i => get('competitionType.id', i) === get('id', competitionType));

  return flow(
    map(s => s.challengeRuleType),
    filter('id'),
    uniqBy('id')
  )(slates);
}

function filterStartTimes(items, competitionType, challengeRuleType) {
  const slates = items
    .filter(i => get('competitionType.id', i) === get('id', competitionType))
    .filter(i => get('challengeRuleType.id', i) === get('id', challengeRuleType));

  return flow(
    map(({ name, startTime: value }) => ({ name, value })),
    sortBy('value')
  )(slates);
}

const UploadResults = styled('div')({});

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing.xsmall
}));

const CountdownWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing.small
}));

const Item = styled('div')(({ kind, theme }) => ({
  display: 'flex',
  backgroundColor: theme.color.white,
  padding: theme.spacing.small,
  color: theme.color.black,
  '&::before': {
    content: kind === 'success' ? '"\\f120"' : '"\\f128"',
    color: kind === 'success' ? theme.color.success : theme.color.danger,
    display: 'inline-block',
    fontFamily: 'Ionicons',
    fontSize: 19,
    paddingRight: theme.spacing.small
  }
}));

const StyledIcon = styled(Icon)({
  fontSize: '1.2em'
});

const ButtonLabel = styled('span')(({ theme }) => ({
  marginRight: theme.spacing.xsmall
}));

const StyledCSVUploadButton = styled(CSVUploadButton)({
  width: '50%',
  height: '100%',
  overflow: 'hidden'
});

const SelectContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap'
});

const RuleFlexiSelect = styled(FlexiSelect)(({ theme }) => ({
  flexBasis: '100%',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '30%'
  }
}));

const TimeFlexiSelect = styled(FlexiSelect)(({ theme }) => ({
  flexBasis: '100%',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '70%'
  }
}));

class CSVPage extends React.Component<Props, State> {
  props: Props;

  state: State = {
    selectedCompetitionType: undefined,
    selectedChallengeRuleType: undefined,
    selectedStartTime: undefined,
    selectedSlate: undefined,
    downloading: false,
    isFileRejected: false,
    uploading: false,
    succeededLineups: undefined,
    failedLineups: undefined,
    totalLineups: undefined
  };

  static defaultProps = {
    fileName: 'Salaries',
    downloadButtonLabel: 'Download CSV',
    uploadLabel: 'Upload Lineups'
  };

  componentDidMount() {
    const { onFetch } = this.props;
    onFetch();
  }

  filterSlates = () => {
    const { slates } = this.props;
    const { selectedCompetitionType, selectedChallengeRuleType, selectedStartTime } = this.state;

    this.setState({
      selectedSlate: slates
        .filter(
          s =>
            !selectedCompetitionType || get('competitionType.id', s) === selectedCompetitionType.id
        )
        .filter(
          s =>
            !selectedChallengeRuleType ||
            get('challengeRuleType.id', s) === selectedChallengeRuleType.id
        )
        .filter(
          s =>
            !selectedStartTime ||
            (s.startTime === selectedStartTime.value && s.name === selectedStartTime.name)
        )
    });
  };

  handleDownloadCSV = () => {
    const { selectedSlate, selectedCompetitionType } = this.state;
    const { downloadFunc, siteName, fileName } = this.props;

    if (!this.canDownload()) {
      return;
    }

    this.setState(
      {
        downloading: true,
        downloadError: undefined
      },
      () => {
        downloadFunc({ slate_id: get('id', head(selectedSlate)) })
          .then(blob => {
            download(
              blob,
              `${siteName}_${selectedCompetitionType.name}_${fileName}.csv`,
              'text/csv'
            );

            this.setState({ downloading: false });
          })
          .catch(({ errors }) => {
            this.setState({
              downloadError: get('message', errors) || 'Something went wrong',
              downloading: false
            });
          });
      }
    );
  };

  handleDocumentDrop = (accepted: Array<Object>, rejected: Array<Object>) => {
    const { uploadFunc } = this.props;
    const file = head(accepted);

    this.setState(
      {
        isFileRejected: !!head(rejected),
        succeededLineups: undefined,
        failedLineups: undefined,
        totalLineups: undefined
      },
      () => {
        if (file) {
          fileToDataUrl(file).then(dataUrl => {
            this.setState({
              uploading: true
            });

            uploadFunc({ lineup_csv_file: dataUrl })
              .then(({ data: { total, successes, failures } }) => {
                this.setState({
                  succeededLineups: successes,
                  failedLineups: failures,
                  totalLineups: total,
                  uploading: false
                });
              })
              .catch(({ errors }) => {
                this.setState({
                  failedLineups: [get('message', errors) || 'Something went wrong'],
                  uploading: false
                });
              });
          });
        }
      }
    );
  };

  canDownload = () => {
    const {
      downloading,
      selectedChallengeRuleType,
      selectedCompetitionType,
      selectedStartTime,
      selectedSlate
    } = this.state;

    return (
      !downloading &&
      selectedCompetitionType &&
      selectedChallengeRuleType &&
      selectedStartTime &&
      Array.isArray(selectedSlate) &&
      selectedSlate.length > 0
    );
  };

  canUpload = () => {
    const { uploading } = this.state;

    return !uploading;
  };

  render() {
    const { competitionTypes, downloadButtonLabel, slates, onRedirect, uploadLabel } = this.props;
    const {
      downloadError,
      downloading,
      isFileRejected,
      selectedChallengeRuleType,
      selectedCompetitionType,
      selectedStartTime,
      totalLineups,
      succeededLineups,
      failedLineups,
      uploading
    } = this.state;
    const filteredChallengeRuleTypes = filterChallengeRuleTypes(slates, selectedCompetitionType);
    const filteredStartTimes = filterStartTimes(
      slates,
      selectedCompetitionType,
      selectedChallengeRuleType
    );

    return (
      <div>
        <Helmet>{t('Upload Lineup')}</Helmet>
        <Rows desktop={2} mobile={1}>
          <Column>
            <Section title="Select Competition">
              <FlexiSelect
                loading={false}
                onChange={value =>
                  this.setState(
                    {
                      selectedCompetitionType: competitionTypes.find(ct => ct.id === value),
                      selectedChallengeRuleType: undefined,
                      selectedStartTime: undefined
                    },
                    this.filterSlates
                  )
                }
                options={competitionTypes.map(({ id, name, sportIcon }) => ({
                  value: id,
                  label: name,
                  icon: `icon-sport-${sportIcon}`
                }))}
                placeholder="Select Competition"
                value={get('id', selectedCompetitionType)}
              />
            </Section>
            {!!selectedCompetitionType && (
              <Section title="Select Slate">
                <SelectContainer>
                  <RuleFlexiSelect
                    loading={false}
                    onChange={value =>
                      this.setState(
                        {
                          selectedChallengeRuleType: filteredChallengeRuleTypes.find(
                            rt => rt.id === value
                          )
                        },
                        this.filterSlates
                      )
                    }
                    options={filteredChallengeRuleTypes.map(({ id, name }) => ({
                      value: id,
                      label: name
                    }))}
                    placeholder="Select Rule"
                    value={get('id', selectedChallengeRuleType)}
                  />
                  {!!selectedChallengeRuleType && (
                    <TimeFlexiSelect
                      loading={false}
                      onChange={value =>
                        this.setState(
                          {
                            selectedStartTime: filteredStartTimes.find(
                              st => `${st.value}-${st.name}` === value
                            )
                          },
                          this.filterSlates
                        )
                      }
                      options={filteredStartTimes.map(st => ({
                        value: `${st.value}-${st.name}`,
                        label: `${moment(st.value).format('DD/MM/YYYY - h:mm a')} ${st.name}`
                      }))}
                      placeholder="Select Start Time"
                      value={
                        selectedStartTime
                          ? `${get('value', selectedStartTime)}-${get('name', selectedStartTime)}`
                          : undefined
                      }
                    />
                  )}
                </SelectContainer>
              </Section>
            )}
            <Button
              autoBorderRadius={false}
              kind="success"
              size="medium"
              disabled={!this.canDownload()}
              onClick={this.handleDownloadCSV}
            >
              <div className="buttonInner">
                {downloading && <Spinner inlineLeft />}
                <ButtonLabel>
                  {t(`${downloading ? 'Downloading CSV...' : downloadButtonLabel}`)}
                </ButtonLabel>
                {!downloading && <StyledIcon type="icon_download" />}
              </div>
            </Button>
          </Column>
          <Column>
            <Section
              title={uploadLabel}
              titleStyle={{ paddingRight: 0 }}
              rightPad={false}
              renderRightElement={() => (
                <StyledCSVUploadButton
                  disabled={!this.canUpload()}
                  maxSize={1024000}
                  onDrop={this.handleDocumentDrop}
                  uploading={uploading}
                />
              )}
            >
              {succeededLineups > 0 && (
                <UploadResults>
                  <Item kind="success">
                    SUCCESSFULLY UPLOADED: {succeededLineups} / {totalLineups} LINEUPS
                  </Item>
                </UploadResults>
              )}
            </Section>
            {Array.isArray(failedLineups) && failedLineups.length > 0 && (
              <Section title="Unsuccessful">
                <UploadResults>
                  {failedLineups.map(error => (
                    <Item key={error} kind="error">
                      {error}
                    </Item>
                  ))}
                </UploadResults>
              </Section>
            )}
            {isFileRejected && (
              <Section title="Unsuccessful">
                <UploadResults>
                  <Item key="rejected" kind="error">
                    Cannot upload this file. It is either too large or not an accepted file type.
                  </Item>
                </UploadResults>
              </Section>
            )}
            {succeededLineups > 0 && (
              <ButtonContainer>
                <Button kind="success" size="medium" link="/my-teams/upcoming-teams">
                  View My Teams
                </Button>
                {succeededLineups === totalLineups && (
                  <CountdownWrapper>
                    <Countdown
                      starting={moment().add(5, 's')}
                      status="normal"
                      format="ss"
                      label=""
                      onTimeOut={onRedirect}
                    />
                  </CountdownWrapper>
                )}
              </ButtonContainer>
            )}
          </Column>
        </Rows>
        <NotificationHandler
          afterCond={!downloading && !downloadError}
          beforeCond={downloading}
          type="success"
        >
          <span>
            Downloading, choose &quot;save&quot; if prompted
            <br />
          </span>
        </NotificationHandler>
        <NotificationHandler
          afterCond={!downloading && !!downloadError}
          beforeCond={downloading}
          type="danger"
        >
          <span>
            {downloadError}
            <br />
          </span>
        </NotificationHandler>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    siteName: state.settings.siteName
  };
}

const actions = {
  // getMyLineUps: myTeamsActions.getMyLineUps,
  // resetMyTeams: myTeamsActions.resetMyTeams,
};

const connector = connect(mapStateToProps, actions);

const apiWrapper = compose(
  withAPICollection({
    fetcher: getSlates,
    normalizeData: map(decodeSlate)
  }),
  withProps(props => ({
    slates: props.items,
    competitionTypes: props.items
      ? flow(
          uniqBy('id'),
          sortBy('name')
        )(props.items.map(({ competitionType }) => competitionType))
      : [],
    challengeRuleTypes: props.items ? filterChallengeRuleTypes(props.items) : [],
    startTimes: props.items ? filterStartTimes(props.items) : []
  }))
);

export default compose(apiWrapper, connector)(CSVPage);
